import React, { useCallback, useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { useAuth } from "../context/AuthContext"
import { useLanguage } from "../context/LanguageContext"
import { useTheme } from "../context/ThemeContext"
import UniverseScene from "../components/UniverseScene"
import MilkyWay from "../components/MilkyWay"

const SKILL_KEYS = ["reading", "writing", "listening", "speaking"]

const SKILL_ROUTES = {
  reading: "/reading",
  writing: "/writing",
  listening: "/listening",
  speaking: "/speaking",
}

const SKILL_LABELS = {
  reading: "Reading",
  writing: "Writing",
  listening: "Listening",
  speaking: "Speaking",
}

const SKILL_DESC_KEYS = {
  reading: "homeReadingDesc",
  writing: "homeWritingDesc",
  listening: "homeListeningDesc",
  speaking: "homeSpeakingDesc",
}

const HERO_LINE = "The Best Place"
const HERO_TO = "to"
const HERO_AFTER = ", Learn, and Grow"
const HERO_VERBS = {
  reading: "Read",
  writing: "Write",
  listening: "Listen",
  speaking: "Speak",
}

const HERO_STATS = [
  { value: "100+", label: "homeHeroStatLessons" },
  { value: "4+", label: "homeHeroStatSkills" },
  { value: "24/7", label: "homeHeroStatAnytime" },
  { value: "A1–C2", label: "homeHeroStatLevels" },
  { value: "3", label: "homeHeroStatLanguages" },
  { value: "Free", label: "homeHeroStatFree" },
  { value: "500+", label: "homeHeroStatVocab" },
  { value: "50+", label: "homeHeroStatExercises" },
  { value: "100+", label: "homeHeroStatGrammar" },
  { value: "10+", label: "homeHeroStatTopics" },
  { value: "AI-Powered", label: "homeHeroStatPersonal" },
  { value: "5–10 min", label: "homeHeroStatDaily" },
  { value: "1 Platform", label: "homeHeroStatPlatform" },
]

function starColor(color, alpha) {
  const match = String(color).match(/rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/)
  if (!match) return `rgba(22, 50, 74, ${alpha})`
  return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${alpha})`
}

function HeroStars() {
  const canvasRef = useRef(null)
  const { isDark } = useTheme()

  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext("2d")
    if (!canvas || !ctx) return undefined

    const stars = Array.from({ length: 90 }, () => ({
      x: Math.random(),
      y: Math.random(),
      r: Math.random() < 0.08 ? 1.35 : 0.35 + Math.random() * 0.7,
      vx: (Math.random() - 0.5) * 0.018,
      vy: -(Math.random() * 0.022 + 0.008),
      alpha: 0.07 + Math.random() * 0.08,
      phase: Math.random() * Math.PI * 2,
      twinkle: 0.4 + Math.random() * 0.6,
    }))
    let frame = 0
    let time = 0
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    const resize = () => {
      const parent = canvas.parentElement
      const width = parent?.clientWidth || canvas.clientWidth || 1
      const height = parent?.clientHeight || canvas.clientHeight || 1
      const ratio = window.devicePixelRatio || 1
      canvas.width = Math.max(1, Math.round(width * ratio))
      canvas.height = Math.max(1, Math.round(height * ratio))
      if (typeof ctx.setTransform === "function") ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
    }

    const draw = () => {
      const width = canvas.clientWidth || canvas.parentElement?.clientWidth || 1
      const height = canvas.clientHeight || canvas.parentElement?.clientHeight || 1
      const color = getComputedStyle(canvas).color
      ctx.clearRect(0, 0, width, height)
      stars.forEach((star) => {
        if (!reduceMotion) {
          star.x += star.vx / 240
          star.y += star.vy / 240
          if (star.y < -0.03) star.y = 1.03
          if (star.x < -0.03) star.x = 1.03
          if (star.x > 1.03) star.x = -0.03
        }
        const pulse = reduceMotion ? 1 : 0.75 + Math.sin(time * star.twinkle + star.phase) * 0.25
        ctx.beginPath()
        ctx.arc(star.x * width, star.y * height, star.r, 0, Math.PI * 2)
        ctx.fillStyle = starColor(color, star.alpha * pulse)
        ctx.fill()
      })
      if (!reduceMotion) time += 0.012
    }

    const tick = () => {
      draw()
      frame = window.requestAnimationFrame(tick)
    }

    resize()
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(resize) : null
    if (canvas.parentElement) observer?.observe(canvas.parentElement)
    window.addEventListener("resize", resize)
    if (reduceMotion) draw()
    else tick()

    return () => {
      window.removeEventListener("resize", resize)
      observer?.disconnect()
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [isDark])

  return <canvas ref={canvasRef} className="home-hero-stars" aria-hidden />
}

function nextSkill(current) {
  const idx = SKILL_KEYS.indexOf(current)
  return SKILL_KEYS[(idx + 1) % SKILL_KEYS.length]
}

function SkillPreview({ skill }) {
  return (
    <div className="home-preview" key={skill}>
      <div className="home-preview-head">
        <span className="home-preview-tag">Live preview</span>
        <span className="home-preview-skill">{SKILL_LABELS[skill]}</span>
      </div>

      <div className="home-preview-body">
        {skill === "reading" && (
          <div className="home-preview-reading">
            <p>The expedition crossed the valley before sunrise.</p>
            <p>
              They reached the <mark>ancient ridge</mark> at dawn.
            </p>
          </div>
        )}

        {skill === "writing" && (
          <div className="home-preview-writing">
            <p className="home-preview-writing-draft">I have working in tech since three years.</p>
            <div className="home-preview-writing-arrow" aria-hidden />
            <p className="home-preview-writing-fix">I have been working in tech for three years.</p>
          </div>
        )}

        {skill === "listening" && (
          <div className="home-preview-listening">
            <div className="home-preview-wave" aria-hidden>
              {Array.from({ length: 12 }).map((_, i) => (
                <span key={i} className="home-preview-wave-bar" style={{ "--bar-i": i }} />
              ))}
            </div>
            <p className="home-preview-listening-cue">Listen and complete the sentence</p>
          </div>
        )}

        {skill === "speaking" && (
          <div className="home-preview-speaking">
            <p className="home-preview-speaking-q">Introduce yourself in 30 seconds.</p>
            <p className="home-preview-speaking-a">&quot;Hi, I&apos;m Maya — I design learning tools.&quot;</p>
            <div className="home-preview-speaking-meter" aria-hidden>
              {Array.from({ length: 16 }).map((_, i) => (
                <span key={i} className="home-preview-speaking-bar" style={{ "--bar-i": i }} />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="home-preview-foot">
        <span>Session accuracy</span>
        <span className="home-preview-score tabular-nums">92%</span>
      </div>
    </div>
  )
}

export default function Home() {
  const { user } = useAuth()
  const { t } = useLanguage()
  const shapesRef = useRef(null)
  const pageRef = useRef(null)
  const [activeSkill, setActiveSkill] = useState("reading")
  const [skillHold, setSkillHold] = useState(false)

  useEffect(() => {
    const root = pageRef.current
    if (!root) return undefined
    const nodes = Array.from(root.querySelectorAll("[data-reveal]"))
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (reduceMotion || typeof IntersectionObserver !== "function") {
      nodes.forEach((node) => node.classList.add("is-visible"))
      return undefined
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          entry.target.classList.add("is-visible")
          observer.unobserve(entry.target)
        })
      },
      { threshold: 0.12, rootMargin: "0px 0px -4% 0px" }
    )
    nodes.forEach((node) => observer.observe(node))
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (skillHold) return undefined
    const id = window.setInterval(() => {
      setActiveSkill((current) => nextSkill(current))
    }, 2500)
    return () => window.clearInterval(id)
  }, [skillHold])

  useEffect(() => {
    const onMove = (e) => {
      const layer = shapesRef.current
      if (!layer) return
      const x = (e.clientX / window.innerWidth - 0.5) * 2
      const y = (e.clientY / window.innerHeight - 0.5) * 2
      layer.style.transform = `rotateX(${y * -0.6}deg) rotateY(${x * 1.2}deg)`
    }
    window.addEventListener("mousemove", onMove)
    return () => window.removeEventListener("mousemove", onMove)
  }, [])

  const handleSkillSelect = useCallback((key) => {
    setActiveSkill(key)
    setSkillHold(true)
  }, [])

  const primaryCta = user ? (
    <Link to="/dashboard" viewTransition className="home-btn home-btn--primary">
      {t("goToDashboard")}
    </Link>
  ) : (
    <>
      <Link to="/signup" viewTransition className="home-btn home-btn--primary">
        {t("startLearningFree")}
      </Link>
      <Link to="/login" viewTransition className="home-btn home-btn--text">
        {t("signIn")}
      </Link>
    </>
  )

  const skillLink = user ? SKILL_ROUTES[activeSkill] : "/signup"

  return (
    <div className="home-shell min-h-screen bg-background relative overflow-x-clip">
      <UniverseScene layerRef={shapesRef} />

      <section className="home-hero relative z-10">
        <HeroStars />
        <MilkyWay />
        <div className="home-hero-copy">
          <p className="home-hero-kicker">
            {t("homeHeroKicker")}
          </p>
          <h1 className="home-hero-billboard" aria-label={`${HERO_LINE} ${HERO_TO} ${HERO_VERBS[activeSkill]}${HERO_AFTER}`}>
            <span className="home-hero-billboard-line">{HERO_LINE}</span>
            <span className="home-hero-billboard-line">
              <span className="home-hero-to">{HERO_TO} </span>
              <span key={activeSkill} className="home-hero-verb">
                <span className="home-hero-verb-letters" aria-hidden>
                  {Array.from(HERO_VERBS[activeSkill]).map((char, index) => (
                    <span key={`${activeSkill}-${index}`} className="home-hero-char" style={{ "--i": index }}>
                      {char}
                    </span>
                  ))}
                </span>
                <span className="sr-only">{HERO_VERBS[activeSkill]}</span>
                <span className="home-hero-verb-bar" aria-hidden />
              </span>
              <span>{HERO_AFTER}</span>
            </span>
          </h1>
          <p className="home-hero-lead">{t("homeHeroStory")}</p>
          <div className="home-hero-mid">
            <p className="home-hero-sub">{t("homeSubtitle")}</p>
            <div className="home-hero-cta">{primaryCta}</div>
          </div>
        </div>
        <div className="home-hero-stats" aria-label={t("homeHeroStatLessons")}>
          <div className="home-stats-track">
            {[0, 1].map((copy) => (
              <div key={copy} className="home-stats-group" aria-hidden={copy === 1 ? true : undefined}>
                {HERO_STATS.map((stat) => (
                  <div key={`${copy}-${stat.label}`} className="home-stat">
                    <span className="home-stat-num tabular-nums">{stat.value}</span>
                    <span className="home-stat-label">{t(stat.label)}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      <div ref={pageRef} className="home-page relative z-10">

        {/* Skills explorer */}
        <section className="home-section home-reveal" data-reveal="left">
          <div className="home-section-head">
            <h2 className="home-section-title">{t("homeExploreSkills")}</h2>
            <p className="home-section-desc">{t("homeFeaturesSubtitle")}</p>
          </div>

          <div className="home-skill-strip" role="tablist" aria-label={t("homeExploreSkills")}>
            {SKILL_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={activeSkill === key}
                className={`home-skill-card ${activeSkill === key ? "home-skill-card--active" : ""}`}
                onClick={() => handleSkillSelect(key)}
              >
                <span className="home-skill-card-name">{t(key)}</span>
                <span className="home-skill-card-desc">{t(SKILL_DESC_KEYS[key])}</span>
              </button>
            ))}
          </div>
          <div className="home-skill-follow">
            <SkillPreview skill={activeSkill} />
            <Link to={skillLink} viewTransition className="home-skill-cta">
              {user ? t("homePracticeSkill") : t("startLearningFree")} →
            </Link>
          </div>
        </section>

        {/* Path */}
        <section className="home-section home-section--path home-reveal" data-reveal="right">
          <div className="home-section-head home-section-head--center">
            <h2 className="home-section-title">{t("homeHowItWorksTitle")}</h2>
            <p className="home-section-desc">{t("homeHowItWorksSubtitle")}</p>
          </div>

          <ol className="home-path">
            {[
              { title: t("homeStep1Title"), desc: t("homeStep1Desc") },
              { title: t("homeStep2Title"), desc: t("homeStep2Desc") },
              { title: t("homeStep3Title"), desc: t("homeStep3Desc") },
            ].map((step) => (
              <li key={step.title} className="home-path-step">
                <div className="home-path-content">
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* Benefits bento */}
        <section className="home-section home-reveal" data-reveal="left">
          <div className="home-section-head home-section-head--center">
            <h2 className="home-section-title">{t("homeWhyChooseTitle")}</h2>
            <p className="home-section-desc">{t("homeWhyChooseSubtitle")}</p>
          </div>

          <div className="home-bento">
            {[
              { title: t("homeBenefit1Title"), desc: t("homeBenefit1Desc"), wide: true },
              { title: t("homeBenefit2Title"), desc: t("homeBenefit2Desc"), wide: true },
              { title: t("homeBenefit3Title"), desc: t("homeBenefit3Desc") },
              { title: t("homeBenefit4Title"), desc: t("homeBenefit4Desc") },
              { title: t("homeBenefit5Title"), desc: t("homeBenefit5Desc") },
              { title: t("homeBenefit6Title"), desc: t("homeBenefit6Desc") },
            ].map((item) => (
              <article
                key={item.title}
                className={`home-bento-card ${item.wide ? "home-bento-card--wide" : ""}`}
              >
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </article>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="home-section home-cta home-reveal" data-reveal="right">
          <div className="home-cta-card">
            <div className="home-cta-copy">
              <p className="home-cta-eyebrow">{t("homeBadge")}</p>
              <h2 className="home-cta-title">{t("homeCTATitle")}</h2>
              <p className="home-cta-desc">{t("homeCTASubtitle")}</p>
            </div>
            <div className="home-cta-actions">
              {primaryCta}
              <Link to="/feedback" viewTransition className="home-btn home-btn--ghost">
                {t("submitSuggestion")}
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
